<template>
  <div class="employee-search">
    <Autosuggest
      :model="searchEmployee"
      :suggestions="filteredEmployees"
      :placeholder="placeholder"
      :limit="limit"
      :disabled="disabled"
      @select="onSelectedEmployee"
      @change="onSearchEmployeeChange"
      suggestionName="suggestionName"
    />
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { xoa_dau } from '@/utils/common';
import { handleError } from '../../../utils/common';

export default {
  name: 'EmployeeSearch',
  components: {
    Autosuggest,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    storeId: {
      type: [Number],
      default: null,
    },
    employeeId: {
      type: [Number, String],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'tên hoặc mã nhân viên',
    },
    limit: {
      type: Number,
      default: 10,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchEmployee: '',
      employees: [],
      filteredEmployees: [],
      selectedEmployeeId: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.searchEmployee) {
          this.searchEmployee = newVal || '';
        }
      },
    },
    storeId: {
      handler() {
        this.employees = [];
        this.filteredEmployees = [];
        this.selectedEmployeeId = null;
        this.searchEmployee = '';
        this.$emit('employee-selected', null);
        this.fetchEmployees();
      },
    },
    employeeId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Wait for employees to be loaded
          if (this.employees.length > 0) {
            this.initializeSelectedEmployee();
          } else {
            // If employees not loaded yet, wait for fetchEmployees to complete
            this.$nextTick(() => {
              if (this.employees.length > 0) {
                this.initializeSelectedEmployee();
              }
            });
          }
        }
      },
    },
  },
  created() {
    this.fetchEmployees();
  },
  methods: {
    fetchEmployees: async function () {
      try {
        const params = this.storeId ? { storeId: this.storeId } : undefined;
        const response = await ApiService.get('/v1/employees/all', { params });
        const employees = response?.data?.data || [];
        this.employees = employees.map((employee) => {
          const { id, fullName, code } = employee;
          let suggestionName = fullName;
          if (code) {
            suggestionName += ` (${code})`;
          }
          return {
            id,
            name: fullName,
            code: code || '',
            storeName: employee.storeName,
            suggestionName,
            nameNoneSign: xoa_dau(fullName),
          };
        });
        this.filteredEmployees = [...this.employees];

        if (this.employeeId) {
          this.initializeSelectedEmployee();
        }
      } catch (error) {
        handleError(error);
      }
    },
    onSelectedEmployee(option) {
      const employee = option.item;
      this.selectedEmployeeId = employee.id;
      this.searchEmployee = employee.name;
      this.$emit('input', employee.name);
      this.$emit('select', employee);
      this.$emit('employee-selected', {
        id: employee.id,
        name: employee.name,
        code: employee.code,
        storeName: employee.storeName,
      });
    },
    onSearchEmployeeChange(text) {
      if (!text) {
        text = '';
      }

      // If had a selected employee and now clearing/changing, emit clear event
      const hadSelectedEmployee = this.selectedEmployeeId !== null;

      this.searchEmployee = text;
      this.selectedEmployeeId = null;
      this.$emit('input', text);

      // Emit employee-selected with null when clearing selection
      if (hadSelectedEmployee) {
        this.$emit('employee-selected', null);
      }

      const filteredData = this.employees
        .filter((item) => {
          return (
            item.nameNoneSign.toLowerCase().indexOf(text.toLowerCase()) > -1 ||
            item.name.toLowerCase().indexOf(text.toLowerCase()) > -1 ||
            item.code.toLowerCase().indexOf(text.toLowerCase()) > -1
          );
        })
        .slice(0, this.limit);

      this.filteredEmployees = [...filteredData];
    },
    initializeSelectedEmployee() {
      if (!this.employeeId || this.employees.length === 0) return;

      const employee = this.employees.find(
        (emp) =>
          emp.id === this.employeeId || emp.id === String(this.employeeId),
      );
      if (employee) {
        this.selectedEmployeeId = employee.id;
        this.searchEmployee = employee.name;
        this.$emit('input', employee.name);
        this.$emit('select', employee);
        this.$emit('employee-selected', {
          id: employee.id,
          name: employee.name,
          code: employee.code,
          storeName: employee.storeName,
        });
      }
    },
    reset() {
      this.searchEmployee = '';
      this.selectedEmployeeId = null;
      this.filteredEmployees = [...this.employees];
      this.$emit('input', '');
      this.$emit('select', null);
      this.$emit('employee-selected', null);
    },
    getSelectedEmployeeId() {
      return this.selectedEmployeeId;
    },
  },
};
</script>

<style lang="scss" scoped>
.employee-search {
  width: 100%;
}
</style>
